import { Injectable } from "@angular/core";
import { HttpService } from "./utils/http.service";
import { ISalesTicketLinkCandidateModel, ISalesTicketLinkResultModel, ISalesTicketModel } from "@models";

@Injectable()
export class SalesTicketsService {
    constructor(private httpService: HttpService) { }

    // Every open ticket on one pipeline. The API runs the auto-advance pass as part of this call, so a
    // reload is what pulls cards forward after a quote is accepted or a job gets scheduled. Omitting
    // salesPipelineId loads the company's default pipeline.
    async getTicketsForBoard(salesPipelineId?: number, assignedToUserId?: number, salesTicketSourceId?: number): Promise<ISalesTicketModel[]> {
        return await this.httpService.get("/salesTickets/getTicketsForBoard", {
            salesPipelineId: salesPipelineId ?? '',
            assignedToUserId: assignedToUserId ?? '',
            salesTicketSourceId: salesTicketSourceId ?? ''
        });
    }

    async getClosedTickets(): Promise<ISalesTicketModel[]> {
        return await this.httpService.get("/salesTickets/getClosedTickets");
    }

    // linkType: 'quote' | 'order' | 'job' | 'invoice'
    async resolveLink(linkType: string, num: string): Promise<ISalesTicketLinkResultModel> {
        return await this.httpService.get("/salesTickets/resolveLink", { linkType, number: num });
    }

    // Candidates for the Link Existing Record picker: every record of one type this ticket could take,
    // narrowed by the customer and the search box. The same four types resolveLink accepts.
    async searchLinkableRecords(salesTicketId: number, linkType: string, customerId: number,
        customerOnly: boolean, searchString: string, includeLinked: boolean): Promise<ISalesTicketLinkCandidateModel[]> {
        return await this.httpService.get("/salesTickets/searchLinkableRecords", {
            salesTicketId,
            linkType,
            customerId: customerId ?? '',
            customerOnly: customerOnly ?? false,
            searchString: searchString ?? '',
            includeLinked: includeLinked ?? false
        });
    }

    // Attaching writes the link onto the document rather than the ticket, so a ticket can hold as many
    // records as the work actually produced. Both return the refreshed ticket.
    async attachDocument(salesTicketId: number, documentType: string, documentId: number): Promise<ISalesTicketModel> {
        return await this.httpService.post("/salesTickets/attachDocument", { salesTicketId, documentType, documentId });
    }

    async detachDocument(salesTicketId: number, documentType: string, documentId: number): Promise<ISalesTicketModel> {
        return await this.httpService.post("/salesTickets/detachDocument", { salesTicketId, documentType, documentId });
    }

    // Every opportunity for one customer, for the customer profile.
    async getTicketsForCustomer(customerId: number): Promise<ISalesTicketModel[]> {
        return await this.httpService.get("/salesTickets/getTicketsForCustomer", { customerId });
    }

    async getTicket(salesTicketId: number): Promise<ISalesTicketModel> {
        return await this.httpService.get("/salesTickets/getTicket", { salesTicketId });
    }

    // The API owns every audit field on a ticket: createTicket stamps them itself and updateTicket reads
    // them back off the stored row, so none of them is ever read from what we post. A brand-new lead has
    // nothing to put in them either, which is why they go up as null — and a null is exactly what an API
    // build that still declares them as a non-nullable DateTime rejects, killing the save in model
    // binding before the controller ever runs. Leaving them out keeps the save working either way.
    private toSavePayload(model: ISalesTicketModel): any {
        const payload: any = { ...model };

        delete payload.createdDate;
        delete payload.createdByUserId;
        delete payload.lastModifiedDate;
        delete payload.lastModifiedByUserId;
        delete payload.statusChangedDate;
        delete payload.closedDate;
        delete payload.closedByUserId;
        delete payload.deletedDate;
        delete payload.deletedByUserId;

        return payload;
    }

    async createTicket(model: ISalesTicketModel): Promise<ISalesTicketModel> {
        return await this.httpService.post("/salesTickets/createTicket", this.toSavePayload(model));
    }

    // The job page's "+ Pipeline" button. The job (and any of its invoices not already on a ticket) is
    // linked, and the card stays in the chosen column until the paperwork moves it on.
    async createTicketForJob(jobId: number, salesPipelineId: number, salesPipelineStageId: number, title: string): Promise<ISalesTicketModel> {
        return await this.httpService.post("/salesTickets/createTicketForJob", { jobId, salesPipelineId, salesPipelineStageId, title });
    }

    // The ticket a job is on, or null.
    async getTicketForJob(jobId: number): Promise<ISalesTicketModel> {
        return await this.httpService.get("/salesTickets/getTicketForJob", { jobId });
    }

    async updateTicket(model: ISalesTicketModel): Promise<ISalesTicketModel> {
        return await this.httpService.post("/salesTickets/updateTicket", this.toSavePayload(model));
    }

    async updateTicketStage(salesTicketId: number, salesPipelineStageId: number): Promise<ISalesTicketModel> {
        return await this.httpService.post("/salesTickets/updateTicketStage", { salesTicketId, salesPipelineStageId });
    }

    async closeTicket(salesTicketId: number, closeResult: string, closeReason: string): Promise<void> {
        return await this.httpService.post("/salesTickets/closeTicket", { salesTicketId, closeResult, closeReason });
    }

    async reopenTicket(salesTicketId: number): Promise<void> {
        return await this.httpService.post(`/salesTickets/reopenTicket?salesTicketId=${salesTicketId}`, {});
    }

    async deleteTicket(salesTicketId: number): Promise<void> {
        return await this.httpService.post(`/salesTickets/deleteTicket?salesTicketId=${salesTicketId}`, {});
    }

    async restoreTicket(salesTicketId: number): Promise<void> {
        return await this.httpService.post(`/salesTickets/restoreTicket?salesTicketId=${salesTicketId}`, {});
    }
}
